/* Credit Balance Report – standalone
   Shows Name | Email | Phone | Balance
   ─────────────────────────────────── */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Breadcrumb       from '@/components/common/Breadcrumb';
import CustomDropdown   from '@/components/common/CustomDropdown';
import Card             from '@/components/ui/cards/Card';
import CardHeader       from '@/components/ui/cards/CardHeader';
import { cn }           from '@/lib/utils';
import validationErrors from '@/lib/validationErrors';

import DataTableFilter  from '../sales/component/DataTableFilter';
import DataTable        from './component/DataTable';
import PritntPdfTable   from '@/components/common/PritntPdfTable';

import { getCreditBalanceReportAllService } from '@/services/CustomerServices';

const DEBUG = false;
const log   = (...a) => DEBUG && console.log('[CreditBalance]', ...a);

export default function CreditBalanceReportsPage({ pageTitle }) {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } =
    PritntPdfTable('Credit Balance Report');

  /* state */
  const [rows,      setRows]      = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [isOpen,    setIsOpen]    = useState(false);

  const filterDivRef = useRef(null);
  const [filter, setFilter] = useState({ search:'', page:1, limit:10 });

  /* fetch */
  useEffect(() => {
    setLoading(true);

    dispatch(getCreditBalanceReportAllService({
      page: filter.page,
      limit: filter.limit,
      search: filter.search,
    }))
      .then(res => {
        const payload = res?.data?.data || {};
        log('API →', payload);

        const list = payload.customers || [];
        const mapped = list.map((c,i) => ({
          _id     : c._id || i,
          name    : c.name,
          email   : c.email,
          phone   : c.phone,
          balance : c.balance,
        }));

        setRows(mapped);
        setTotalRows(payload.summary?.totalRows || mapped.length);
      })
      .catch(err => {
        const { message, errorData } = err || {};
        validationErrors(errorData, message);
      })
      .finally(() => setLoading(false));
  }, [dispatch, filter]);

  /* ui helpers */
  const toggleFilter = () => {
    if (!filterDivRef.current) return;
    filterDivRef.current.style.height = isOpen ? '0px' : 'auto';
    setIsOpen(!isOpen);
  };

  const onChangeFilter = (name, value) => {
    if (name === 'search') setFilter(p => ({ ...p, page:1, search:value }));
    else if (name === 'page') setFilter(p => ({ ...p, page:value }));
    else if (name === 'limit') setFilter(p => ({ ...p, page:1, limit:value }));
  };

  /* render */
  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <button className="db-card-filter-btn table-filter-btn" onClick={toggleFilter}>
              <i className="lab lab-filter lab-font-size-14" /><span>Filter</span>
            </button>
            <CustomDropdown
              buttonText="Export" buttonIcon="lab-file-export"
              dropdownList={[{ icon:'lab-printer-line', text:'Print', dropDownAction:handleDownloadPdf }]}
            />
          </div>
        </CardHeader>

        {/* search bar */}
        <div
          ref={filterDivRef}
          className={cn('table-filter-div overflow-hidden transition-height', isOpen && 'active')}
        >
          <DataTableFilter
            onChangeFilter={(n,v)=>onChangeFilter('search', v)}
            hideDateFilter
          />
        </div>

        {/* table */}
        <div ref={printTableRef}>
          <DataTable
            isLoading    ={loading}
            dataSource   ={rows}
            totalRecord  ={totalRows}
            dataFilter   ={filter}
            onChangePage ={onChangeFilter}
          />
        </div>
      </Card>
    </>
  );
}
